// src/components/ui/Confirmation.tsx — SCE Issues
// Une confirmation aux couleurs de l'appli, à la place du `window.confirm` du
// navigateur (06/10, retour de Guillaume : « une popup du site qui s'intègre
// mieux dans le thème »). Premier usage : abandonner une NC-S.
//
// Au-dessus de tout (fiche en surimpression, fenêtre NC-S). Le bouton sûr
// (« garder ») a le focus : Entrée par réflexe ne détruit rien. Échap ou un
// clic sur le fond = annuler.

import { useEffect, useRef, type JSX, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useClicFond } from '@/hooks/useClicFond'

export interface ConfirmationProps {
  titre: string
  children: ReactNode
  /** Libellé de l'action (« Abandonner »). */
  confirmer: string
  /** Libellé du retour (« Continuer la saisie »). */
  annuler?: string
  /** Action destructrice : bouton rouge. */
  danger?: boolean
  /** Bouton de l'action grisé (26/10 : vider les listes FNC-T tant que « VIDER » n'est pas tapé). */
  confirmerDesactive?: boolean
  /**
   * Une troisième issue, entre « annuler » et « confirmer » (15/10 : fermer une
   * FNC-T en cours → « Abandonner » à côté de « Garder en brouillon »).
   */
  autre?: { libelle: string; danger?: boolean; onClick: () => void }
  onConfirmer: () => void
  onAnnuler: () => void
}

export function Confirmation({
  titre,
  children,
  confirmer,
  annuler = 'Annuler',
  danger,
  confirmerDesactive,
  autre,
  onConfirmer,
  onAnnuler,
}: ConfirmationProps): JSX.Element {
  const garder = useRef<HTMLButtonElement>(null)
  const fond = useClicFond(onAnnuler)

  useEffect(() => {
    garder.current?.focus()
    // Échap ferme la confirmation — et seulement elle.
    function surEchap(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      onAnnuler()
    }
    window.addEventListener('keydown', surEchap, true)
    return () => window.removeEventListener('keydown', surEchap, true)
  }, [onAnnuler])

  return createPortal(
    <div className="confirmation-voile" {...fond} role="presentation">
      <div className="confirmation" role="alertdialog" aria-modal="true" aria-labelledby="confirmation-titre" aria-describedby="confirmation-texte">
        <h2 id="confirmation-titre" className="confirmation-titre">{titre}</h2>
        <div id="confirmation-texte" className="confirmation-texte">{children}</div>
        <div className="confirmation-boutons">
          <button ref={garder} type="button" className="confirmation-annuler" onClick={onAnnuler}>
            {annuler}
          </button>
          {autre && (
            <button type="button" className="confirmation-confirmer" data-danger={autre.danger} onClick={autre.onClick}>
              {autre.libelle}
            </button>
          )}
          <button type="button" className="confirmation-confirmer" data-danger={danger} disabled={confirmerDesactive} onClick={onConfirmer}>
            {confirmer}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
